import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import {
  signOut as webSignOut,
  sendEmailVerification as webSendEmailVerification,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
} from "firebase/auth";
import { auth } from "../firebase";
import { createOrMergeBasicUserProfile } from "./profileDataService";

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();
const SOCIAL_REDIRECT_KEY = "ee_post_social_redirect";

const getPlatform = () => {
  try {
    return Capacitor.getPlatform();
  } catch {
    return "web";
  }
};

const isNativePlatform = () => {
  const platform = getPlatform();
  return platform === "ios" || platform === "android";
};

const mapNativeUser = (user) => {
  if (!user) return null;

  return {
    uid: user.uid || user.userId || null,
    email: user.email || "",
    displayName: user.displayName || "",
    emailVerified: !!user.emailVerified,
    isAnonymous: !!user.isAnonymous,
    providerId:
      user.providerId ||
      (Array.isArray(user.providerData) && user.providerData[0]?.providerId) ||
      null,

    getIdTokenResult: async (forceRefresh = false) => {
      const result = await FirebaseAuthentication.getIdToken({
        forceRefresh,
      });

      const token = result?.token || "";

      if (!token) {
        return { claims: {} };
      }

      try {
        const payload = JSON.parse(
          atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
        );

        return {
          token,
          claims: payload || {},
        };
      } catch {
        return {
          token,
          claims: {},
        };
      }
    },
  };
};

const mapWebUser = (user) => {
  if (!user) return null;

  return {
    uid: user.uid,
    email: user.email || "",
    displayName: user.displayName || "",
    emailVerified: !!user.emailVerified,
    isAnonymous: !!user.isAnonymous,
    providerId: user.providerData?.[0]?.providerId || null,
    getIdTokenResult:
      typeof user.getIdTokenResult === "function"
        ? user.getIdTokenResult.bind(user)
        : undefined,
  };
};

const getCurrentNativeUser = async () => {
  const result = await FirebaseAuthentication.getCurrentUser();
  return mapNativeUser(result?.user || null);
};

export const registerWithEmail = async ({ email, password, username }) => {
  const emailNorm = normalizeEmail(email);

  if (isNativePlatform()) {
    const result = await FirebaseAuthentication.createUserWithEmailAndPassword({
      email: emailNorm,
      password,
    });

    const user = mapNativeUser(result?.user);

    if (!user?.uid) {
      throw new Error("no-user-returned");
    }

    await FirebaseAuthentication.sendEmailVerification();

    await createOrMergeBasicUserProfile({
      uid: user.uid,
      email: user.email || emailNorm,
      username,
      name: username,
    });

    return user;
  }

  const result = await createUserWithEmailAndPassword(auth, emailNorm, password);
  const user = result.user;

  await webSendEmailVerification(user);

  await createOrMergeBasicUserProfile({
    uid: user.uid,
    email: user.email || emailNorm,
    username,
    name: username,
  });

  return mapWebUser(user);
};

export const loginWithEmail = async ({ email, password }) => {
  const emailNorm = normalizeEmail(email);

  console.log("🔐 loginWithEmail start", {
    emailNorm,
    platform: getPlatform(),
  });

  if (isNativePlatform()) {
    const result = await FirebaseAuthentication.signInWithEmailAndPassword({
      email: emailNorm,
      password,
    });

    const user = mapNativeUser(result?.user);

    console.log("🔐 native loginWithEmail success", user);

    return user;
  }

  const result = await signInWithEmailAndPassword(auth, emailNorm, password);
  const user = mapWebUser(result.user);

  console.log("🔐 web loginWithEmail success", user);

  return user;
};

export const logoutUser = async () => {
  if (isNativePlatform()) {
    await FirebaseAuthentication.signOut();
    return;
  }

  await webSignOut(auth);
};

export const resendVerificationForCurrentUser = async () => {
  if (isNativePlatform()) {
    await FirebaseAuthentication.sendEmailVerification();
    return;
  }

  if (!auth.currentUser) return;
  await webSendEmailVerification(auth.currentUser);
};

export const getCurrentAuthUser = async () => {
  if (isNativePlatform()) {
    return await getCurrentNativeUser();
  }

  return mapWebUser(auth.currentUser);
};

export const reloadCurrentAuthUser = async () => {
  if (isNativePlatform()) {
    await FirebaseAuthentication.reload();
    return await getCurrentNativeUser();
  }

  if (!auth.currentUser) return null;
  await auth.currentUser.reload();

  return mapWebUser(auth.currentUser);
};

export const isUserLoggedIn = async () => {
  try {
    const user = await getCurrentAuthUser();
    return !!user?.uid;
  } catch {
    return false;
  }
};

export const getCurrentIdToken = async (forceRefresh = true) => {
  if (isNativePlatform()) {
    const result = await FirebaseAuthentication.getIdToken({
      forceRefresh,
    });

    return result?.token || null;
  }

  if (!auth.currentUser || typeof auth.currentUser.getIdToken !== "function") {
    return null;
  }

  return await auth.currentUser.getIdToken(forceRefresh);
};

export const subscribeToAuthChanges = async (callback) => {
  if (typeof callback !== "function") {
    throw new Error("subscribeToAuthChanges: callback fehlt");
  }

  if (isNativePlatform()) {
    const handle = await FirebaseAuthentication.addListener(
      "authStateChange",
      (event) => {
        callback(mapNativeUser(event?.user || null));
      }
    );

    return async () => {
      await handle.remove();
    };
  }

  return onAuthStateChanged(auth, (user) => {
    callback(mapWebUser(user));
  });
};

export const finalizeSocialLogin = async ({
  user,
  fallbackUsername = "",
  navigateTo = "/browse",
  navigate,
}) => {
  if (!user?.uid) return null;

  const finalName = user.displayName || fallbackUsername || "";
  const finalEmail = normalizeEmail(user.email || "");

  await createOrMergeBasicUserProfile({
    uid: user.uid,
    email: finalEmail,
    username: finalName,
    name: finalName,
  });

  try {
    localStorage.setItem(
      "user",
      JSON.stringify({
        email: user.email || "",
        uid: user.uid,
        username: finalName,
        mode: "firebase",
      })
    );
  } catch (e) {
    console.warn("LocalStorage konnte nicht gesetzt werden:", e);
  }

  try {
    if (isNativePlatform()) {
      const { initPushAfterLogin } = await import("./pushDataService");

      setTimeout(() => {
        initPushAfterLogin({ force: true });
      }, 800);
    }
  } catch (e) {
    console.warn("Native Push konnte nach Social Login nicht gestartet werden:", e);
  }

  // ✅ Wichtig:
  // Nur automatisch navigieren, wenn bewusst ein Ziel übergeben wurde.
  // Bei navigateTo: null bleibt die Kontrolle bei LoginPage/RegisterPage.
  if (
    typeof navigate === "function" &&
    typeof navigateTo === "string" &&
    navigateTo.trim()
  ) {
    navigate(navigateTo);
  }

  return user;
};

export const authenticateWithGoogle = async ({
  fallbackUsername = "",
  navigate,
  navigateTo = "/browse",
}) => {
  if (isNativePlatform()) {
  try {
    console.log("🔐 Native Google Login START", {
      platform: getPlatform(),
    });

    const result = await FirebaseAuthentication.signInWithGoogle();
    const user = mapNativeUser(result?.user);

    console.log("🔐 Native Google Login RESULT", {
      rawUser: result?.user || null,
      mappedUser: user,
    });

    if (!user?.uid) {
      throw new Error("native-google-no-user-returned");
    }

    return await finalizeSocialLogin({
      user,
      fallbackUsername,
      navigate,
      navigateTo,
    });
  } catch (error) {
    console.error("❌ Native Google Login FAILED", {
      code: error?.code || null,
      message: error?.message || null,
      full: error,
    });

    throw error;
  }
}

  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  try {
    const cred = await signInWithPopup(auth, provider);

    return await finalizeSocialLogin({
      user: mapWebUser(cred?.user),
      fallbackUsername,
      navigate,
      navigateTo,
    });
  } catch (e) {
    if (
      e?.code === "auth/popup-blocked" ||
      e?.code === "auth/operation-not-supported-in-this-environment"
    ) {
      localStorage.setItem(SOCIAL_REDIRECT_KEY, navigateTo);
      await signInWithRedirect(auth, provider);
      return null;
    }

    const current = auth.currentUser;
if (current) {
  const uNow = mapWebUser(current);
      return await finalizeSocialLogin({
        user: uNow,
        fallbackUsername,
        navigate,
        navigateTo,
      });
    }

    throw e;
  }
};

export const authenticateWithApple = async ({
  fallbackUsername = "",
  navigate,
  navigateTo = "/browse",
}) => {
  if (isNativePlatform()) {
  try {
    console.log("🍎 Native Apple Login START", {
      platform: getPlatform(),
    });

    const result = await FirebaseAuthentication.signInWithApple();
    const user = mapNativeUser(result?.user);

    console.log("🍎 Native Apple Login RESULT", {
      rawUser: result?.user || null,
      mappedUser: user,
    });

    if (!user?.uid) {
      throw new Error("native-apple-no-user-returned");
    }

    return await finalizeSocialLogin({
      user,
      fallbackUsername,
      navigate,
      navigateTo,
    });
  } catch (error) {
    console.error("❌ Native Apple Login FAILED", {
      code: error?.code || null,
      message: error?.message || null,
      full: error,
    });

    throw error;
  }
}
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");

  try {
    const cred = await signInWithPopup(auth, provider);

    return await finalizeSocialLogin({
      user: mapWebUser(cred?.user),
      fallbackUsername,
      navigate,
      navigateTo,
    });
  } catch (e) {
    if (
      e?.code === "auth/popup-blocked" ||
      e?.code === "auth/operation-not-supported-in-this-environment"
    ) {
      localStorage.setItem(SOCIAL_REDIRECT_KEY, navigateTo);
      await signInWithRedirect(auth, provider);
      return null;
    }

    const uNow = mapWebUser(auth.currentUser);
    if (uNow?.uid) {
      return await finalizeSocialLogin({
        user: uNow,
        fallbackUsername,
        navigate,
        navigateTo,
      });
    }

    throw e;
  }
};

export const getPostSocialRedirectPath = () => {
  return localStorage.getItem(SOCIAL_REDIRECT_KEY) || "/browse";
};

export const clearPostSocialRedirectPath = () => {
  localStorage.removeItem(SOCIAL_REDIRECT_KEY);
};